import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Id, OrgWaybillSettings } from '@hero/schema';
import type { OrderWaybillHtmlPreviewAPI } from '@hero/api';
import { useApi } from '@hero/context';

export type WaybillPreviewDraft = Partial<OrgWaybillSettings>;

export function useWaybillHtmlPreview(params: {
  locale: string;
  previewOrgId?: Id;
  draft?: WaybillPreviewDraft;
}) {
  const api = useApi();
  const [html, setHtml] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = useMemo(() => {
    const locale = params.locale === 'ar' ? 'ar' : 'en';
    return {
      locale,
      orgId: params.previewOrgId,
      accent_color: params.draft?.accent_color,
      use_org_logo: params.draft?.use_org_logo,
      logo_shape: params.draft?.logo_shape,
      logo_background: params.draft?.logo_background,
      logo_size: params.draft?.logo_size,
      logo_rounding: params.draft?.logo_rounding,
    } satisfies Omit<OrderWaybillHtmlPreviewAPI['req'], 'orgId'> & { orgId?: Id };
  }, [
    params.locale,
    params.previewOrgId,
    params.draft?.accent_color,
    params.draft?.use_org_logo,
    params.draft?.logo_shape,
    params.draft?.logo_background,
    params.draft?.logo_size,
    params.draft?.logo_rounding,
  ]);

  const fetchPreview = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Raw HTML response — bypasses the standard `{ data }` JSON envelope,
      // so use `withRes` (returns axios body directly) not `call()`.
      const htmlText = await api.orders.getWaybillHtmlPreview.withRes(
        query as OrderWaybillHtmlPreviewAPI['req'],
        {
        responseType: 'text',
        },
      );
      setHtml(typeof htmlText === 'string' ? htmlText : '');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Preview failed');
      setHtml('');
    } finally {
      setIsLoading(false);
    }
  }, [api.orders, query]);

  useEffect(() => {
    void fetchPreview();
  }, [fetchPreview]);

  return { html, isLoading, error, refetch: fetchPreview };
}
